/**
 * EditorFooter — Word count, block count, reading time, save status.
 */

import { useState, useEffect, useMemo } from 'react';
import { MessageCircle } from 'lucide-react';
import type { BlockNoteEditor as BlockNoteEditorCore } from '@blocknote/core';

interface EditorFooterProps {
  editor: BlockNoteEditorCore | null;
  contentFormat?: string;
  commentCount?: number;
  className?: string;
}

function countWordsFromBlocks(editor: BlockNoteEditorCore): number {
  let words = 0;

  function countBlock(block: any) {
    if (Array.isArray(block.content)) {
      for (const item of block.content) {
        if (item.type === 'text' && item.text) {
          words += item.text.split(/\s+/).filter(Boolean).length;
        }
      }
    }
    if (Array.isArray(block.children)) {
      for (const child of block.children) {
        countBlock(child);
      }
    }
  }

  for (const block of editor.document) {
    countBlock(block);
  }

  return words;
}

export function EditorFooter({ editor, contentFormat, commentCount, className }: EditorFooterProps) {
  const [wordCount, setWordCount] = useState(0);
  const [blockCount, setBlockCount] = useState(0);

  useEffect(() => {
    if (!editor) return;

    const update = () => {
      setWordCount(countWordsFromBlocks(editor));
      setBlockCount(editor.document.length);
    };

    update();
    const unsubscribe = editor.onEditorContentChange(update);
    return () => unsubscribe?.();
  }, [editor]);

  const readingTime = useMemo(() => {
    const minutes = Math.ceil(wordCount / 200);
    return minutes <= 1 ? '< 1 min read' : `${minutes} min read`;
  }, [wordCount]);

  return (
    <div className={`flex items-center justify-between px-6 py-2 border-t border-stone-100 text-xs text-stone-400 ${className || ''}`}>
      <div className="flex items-center gap-4">
        <span>{wordCount.toLocaleString()} words</span>
        <span>{blockCount} blocks</span>
        <span>{readingTime}</span>
        {commentCount != null && commentCount > 0 && (
          <span className="flex items-center gap-1 text-purple-500">
            <MessageCircle size={12} />
            {commentCount} open comment{commentCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {contentFormat && (
          <span>{contentFormat}</span>
        )}
      </div>
    </div>
  );
}

export default EditorFooter;
